import { StickerState } from '@/types'
import { DisplayObjectPair } from '@/pixi/types'
import * as PIXI from 'pixi.js'
import { useProjectStore, resolutionSizes } from '@/store/project-store'
import { MinimumScaleType } from '@/pixi/Transformer'
import useTransformer from '@/composables/UseTransformer'
import { TransformerHandleDataInterface, MouseFlagsInterface } from '@/composables/UseTransformer'
import { useApplicationStore } from '@/store/application-store'
import useLayers from '@/composables/UseLayers'
import { Layers } from '@/static/Layers'
import { AnimatedGIF } from '@pixi/gif'
import { watch } from 'vue'
import useDisplayObject from '@/composables/UseDisplayObject'
import {
  deleteCachedMediaResource,
  getCachedMediaResource,
  loadMediaResource,
  LoadedMediaResource,
  pruneMediaResourceCache
} from '@/pixi/load-media-resource'

const { canvasLayers, getLayerZIndex } = useLayers()
const { createTransformer } = useTransformer()

const { updateDisplayObjState, onTransformDisplayObject } = useDisplayObject()

export default function useStickerCanvas () {
  const projectStore = useProjectStore()
  const applicationStore = useApplicationStore()

  /**
   * Add the sticker display object pair to the canvas and store a reference of it
   */
  const addStickerToCanvas = (
    application: PIXI.Application,
    transformerLayer: PIXI.Container,
    layer: PIXI.Container,
    resource: LoadedMediaResource,
    sticker: StickerState,
    index: number,
    transformerHandleData: TransformerHandleDataInterface,
    mouseFlags: MouseFlagsInterface,
    textDisplayObjectsMap: Map<number, DisplayObjectPair>,
    stickerDisplayObjectsMap: Map<number, DisplayObjectPair>
  ): void => {
    const stickerDispObj = createStickerDisplayObject(
      application,
      transformerLayer,
      resource,
      sticker,
      transformerHandleData,
      mouseFlags,
      textDisplayObjectsMap,
      stickerDisplayObjectsMap
    )
    if (stickerDispObj) {
      layer.addChildAt(stickerDispObj.sprite, index)
      stickerDispObj.transformer.zIndex = getLayerZIndex(layer.zIndex, index, projectStore.stickers.length)
      transformerLayer.addChild(stickerDispObj.transformer)

      stickerDisplayObjectsMap.set(sticker.id, stickerDispObj)
      updateDisplayObjState(sticker, textDisplayObjectsMap, stickerDisplayObjectsMap)
      applicationStore.selectSticker(sticker)
    }
    applicationStore.isStickerLoading = false
  }

  const fitSticker = (stickerSprite: PIXI.Sprite) => {
    // Calculate ratios of the sticker relative to the canvas dimensions
    let widthRatio = projectStore.resolution.width / stickerSprite.width
    let heightRatio = projectStore.resolution.height / stickerSprite.height

    // Choose the smaller ratio - this is the maximum that the sticker needs to scale down to match both the dimensions of the canvas
    let scalingFactor = Math.min(widthRatio, heightRatio)

    // Scale the sticker down if it is larger than the canvas
    if (scalingFactor < 1) {
      stickerSprite.scale.set(scalingFactor * stickerSprite.scale.x, scalingFactor * stickerSprite.scale.y)
    }

    // Scale the sticker up if it is smaller than the minimum width
    const minimumSize = resolutionSizes.portrait.width * 0.05
    if (stickerSprite.width < minimumSize || stickerSprite.height < minimumSize) {
      widthRatio = minimumSize / stickerSprite.texture.width
      heightRatio = minimumSize / stickerSprite.texture.height
      // Choose the larger of the two - this is the maximum the sticker needs to scale up to match the canvas dimensions
      scalingFactor = Math.max(widthRatio, heightRatio)
      stickerSprite.scale.set(scalingFactor, scalingFactor)
    }
  }

  const updateStickers = (
    application: PIXI.Application,
    stickers: StickerState[],
    layer: PIXI.Container,
    transformerLayer: PIXI.Container,
    transformerHandleData: TransformerHandleDataInterface,
    mouseFlags: MouseFlagsInterface,
    stickerDisplayObjectsMap: Map<number, DisplayObjectPair>,
    textDisplayObjectsMap: Map<number, DisplayObjectPair>
  ) => {

    const handleStickerLoadError = (stickers: StickerState[], sticker: StickerState): void => {
      applicationStore.pushSnackbarMessage({
        header: 'Oops something went wrong',
        text: 'Please try again',
        state: 'error'
      })

      deleteCachedMediaResource(sticker.url)
      const spriteIndex = stickers.findIndex(s => s.id === sticker.id)
      if (spriteIndex > -1) {
        stickers.splice(spriteIndex, 1)
      }
      applicationStore.isStickerLoading = false
    }

    const handleResource = (
      stickers: StickerState[],
      resource: LoadedMediaResource,
      sticker: StickerState,
      index: number
    ): void => {
      if (!(resource.animation || resource.texture)) {
        handleStickerLoadError(stickers, sticker)
        return
      }

      try {
        addStickerToCanvas(
          application,
          transformerLayer,
          layer,
          resource,
          sticker,
          index,
          transformerHandleData,
          mouseFlags,
          textDisplayObjectsMap,
          stickerDisplayObjectsMap
        )
      } catch (e) {
        console.error(e)
        applicationStore.pushSnackbarMessage({
          header: 'Oops something went wrong',
          text: 'Please try again',
          state: 'error'
        })
      }
    }

    const allStickers = projectStore.stickers.concat(projectStore.logoStickers)
    // Delete any removed stickers
    for (const [id, { sprite, transformer }] of stickerDisplayObjectsMap.entries()) {
      if (!allStickers.some((sticker) => sticker.id === id)) {
        canvasLayers[Layers.STICKER].removeChild(sprite)
        // This does not delete the loaderResource
        sprite.destroy({ children: true, texture: true, baseTexture: false })
        transformerLayer.removeChild(transformer)
        transformer.destroy(true)
        stickerDisplayObjectsMap.delete(id)
      }
    }

    pruneMediaResourceCache(allStickers.map((sticker) => sticker.url))

    for (const [index, sticker] of stickers.entries()) {
      const stickerDisplayObjects = stickerDisplayObjectsMap.get(sticker.id)

      // If display objects already exist for this sticker, ensure the correct z ordering
      if (stickerDisplayObjects) {
        const { sprite, transformer } = stickerDisplayObjects
        layer.setChildIndex(sprite, index)
        transformer.zIndex = getLayerZIndex(layer.zIndex, index, stickers.length)
      } else {
        // Create a new sticker display objects
        const resource = getCachedMediaResource(sticker.url)
        applicationStore.isStickerLoading = true

        if (resource) {
          handleResource(stickers, resource, sticker, index)
        } else {
          void loadMediaResource(sticker.url)
            .then((loadedResource) => handleResource(stickers, loadedResource, sticker, index))
            .catch((e) => {
              console.error(e)
              handleStickerLoadError(stickers, sticker)
            })
        }
      }
    }
  }

  /**
   * Creates a sprite and its transformer based on a sticker resource
   */
  const createStickerDisplayObject = (
    application: PIXI.Application,
    transformerLayer: PIXI.Container,
    resource: LoadedMediaResource,
    sticker: StickerState,
    transformerHandleData: TransformerHandleDataInterface,
    mouseFlags: MouseFlagsInterface,
    textDisplayObjectsMap: Map<number, DisplayObjectPair>,
    stickerDisplayObjectsMap: Map<number, DisplayObjectPair>
  ): DisplayObjectPair | undefined => {
    let sprite: PIXI.Sprite | AnimatedGIF

    if (resource.animation) {
      // `animation` is populated if it is a GIF, need to clone for multiple copies
      sprite = resource.animation.clone()
    } else if (resource.texture) {
      // `texture` is populated if it is a supported sticker
      sprite = PIXI.Sprite.from(new PIXI.Texture(resource.texture.baseTexture))
    } else {
      console.error(`Incompatible media type from resource: ${sticker.url}`)
      return
    }
    sprite.anchor.set(0.5)
    const transformer = createTransformer(
      [sprite],
      MinimumScaleType.ABSOLUTE,
      application,
      transformerHandleData,
      mouseFlags,
      textDisplayObjectsMap,
      stickerDisplayObjectsMap
    )

    // This watcher toggles the visibility of the bounding box and scale handles
    watch(() => applicationStore.selectedSticker, () => {
      transformer.enabledHandles = applicationStore.selectedSticker === sticker ? ['rotator', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight'] : []
      transformer.boundingBoxes = applicationStore.selectedSticker === sticker ? 'all' : 'none'
      transformer.rotateEnabled = applicationStore.selectedSticker === sticker
    }, { immediate: true })

    transformer.addListener('pointerdown', (e: PIXI.InteractionEvent) => {
      // Prevent secondary (right) click
      if (e.data.button === 2) {
        return
      }

      applicationStore.selectSticker(sticker)
      mouseFlags.isMouseDown = true
    })

    transformer.addListener('mouseover', () => {
      if (applicationStore.selectedSticker !== sticker && !mouseFlags.isMouseDown) {
        transformer.boundingBoxes = 'all'
      }
    })

    transformer.addListener('mouseout', () => {
      if (applicationStore.selectedSticker !== sticker) {
        transformer.boundingBoxes = 'none'
      }
    })

    transformer.addListener('transformchange', () => {
      onTransformDisplayObject(
        sticker,
        textDisplayObjectsMap,
        stickerDisplayObjectsMap,
        transformerLayer,
        transformerHandleData.isTransformerHandleGrabbed,
        projectStore.resolution
      )
    })

    // Make sure the sticker is fitted on initial load
    sprite.scale.set(sticker.scale)
    fitSticker(sprite)

    // Set the position of the sprite, by default centered on canvas
    const positionX = sticker.x ?? application.renderer.width / 2
    const positionY = sticker.y ?? application.renderer.height / 2
    sprite.position.set(positionX, positionY)

    return {
      sprite,
      transformer
    }
  }

  return {
    addStickerToCanvas,
    updateStickers
  }
}
